/**
 * HSE alert builders — Messagerie payloads from Claire Maréchal for any
 * mandatory training entering the expiring window or expiring.
 *
 * 01/06/2026 — Phase 4 (FCO) + Phase 5 (full matrix: medical, ADR, CACES).
 *
 * Idempotency: caller composes the queue key as
 *   `claire-{trainingKey}-{status}-{driverId}-{expiresOn}` so the same
 * mémo never duplicates.
 */

import { HSE_TRAININGS } from './hseCompliance';

const TRAINING_ACTION_FR = {
  fco: 'programmer la formation **FCO** (≈600 € · 2 jours) depuis la fiche RH du chauffeur',
  medical: 'planifier la **visite médicale** (≈200 € · 1 jour) auprès du médecin du travail agréé',
  adr: 'inscrire le chauffeur au **recyclage ADR** (≈1 200 € · 3 jours) — sans cette mise à jour il ne peut plus prendre de mission matières dangereuses',
  caces: 'programmer le **recyclage CACES R489/R372** (≈800 € · 4 jours) pour conserver le droit de manutention engins',
};

const TRAINING_RISK_FR = {
  fco: "votre chauffeur ne peut plus légalement conduire un poids lourd ≥ 3,5 T — vous risquez l'immobilisation du véhicule au premier contrôle DREAL",
  medical: 'la conduite est juridiquement interdite — c\'est un motif de suspension immédiate du contrat de travail et un risque assuranciel majeur',
  adr: 'aucune mission ADR (carburant, chimie, gaz) ne peut être confiée à ce chauffeur — perte sèche de marge premium',
  caces: 'la manutention engin (chariot, gerbeur) lui est interdite — les missions BTP impliquant chargement deviennent inopérables',
};

function trainingLabel(trainingKey) {
  const spec = HSE_TRAININGS[trainingKey];
  if (!spec) return trainingKey;
  return spec.labelFr;
}

/**
 * Generic "training about to expire" mémo (60-day window).
 */
export function buildTrainingExpiringMemo({ trainingKey, driverName, daysRemaining, expiresOn }) {
  const label = trainingLabel(trainingKey);
  return {
    senderRole: 'role-claire-marechal',
    tone: 'warning',
    subject: `${label.split(' — ')[0]} à renouveler — ${driverName}`,
    body:
      `Petit point compliance : la **${label}** de **${driverName}** arrive ` +
      `à échéance dans **${daysRemaining} jours** (expire au J${expiresOn}).\n\n` +
      `Sans renouvellement à temps, ${TRAINING_RISK_FR[trainingKey] || 'la conformité administrative est rompue'}.\n\n` +
      `Action recommandée : ${TRAINING_ACTION_FR[trainingKey] || 'planifier le renouvellement'}.\n\n` +
      `— Claire Maréchal, Responsable QHSE / RSE`,
  };
}

/**
 * Generic "training expired" mémo (day after the actual expiry).
 */
export function buildTrainingExpiredMemo({ trainingKey, driverName, expiresOn }) {
  const label = trainingLabel(trainingKey);
  const isBlocking = HSE_TRAININGS[trainingKey]?.blocking;
  const blockingNote = isBlocking
    ? `\n\n⚠️ **Attention** : tant que ce n'est pas régularisé, ${driverName} ne peut ${
        isBlocking === 'global'
          ? "PAS être affecté(e) à un véhicule (blocage automatique)"
          : "PAS conduire de véhicule ADR (blocage automatique)"
      }.`
    : '';
  const risk = TRAINING_RISK_FR[trainingKey];
  const riskNote = risk ? `\n\nRappel du risque : ${risk}.` : '';
  return {
    senderRole: 'role-claire-marechal',
    tone: 'warning',
    subject: `${label.split(' — ')[0]} expirée — ${driverName}`,
    body:
      `⚠️ Compliance en alerte : la **${label}** de **${driverName}** est ` +
      `**expirée depuis le J${expiresOn}**.${blockingNote}${riskNote}\n\n` +
      `Action prioritaire : ${TRAINING_ACTION_FR[trainingKey] || 'renouveler sans délai'}.\n\n` +
      `— Claire Maréchal, Responsable QHSE / RSE`,
  };
}

// ── Phase 4 backward-compat wrappers (FCO-only API) ──────────────────────

export function buildFcoExpiringMemo({ driverName, daysRemaining, expiresOn }) {
  return buildTrainingExpiringMemo({ trainingKey: 'fco', driverName, daysRemaining, expiresOn });
}

export function buildFcoExpiredMemo({ driverName, expiresOn }) {
  return buildTrainingExpiredMemo({ trainingKey: 'fco', driverName, expiresOn });
}
